/**
 * Die 16 deutschen Bundesländer als URL-Slug → Anzeigename. Feststehende,
 * staatsrechtliche Liste, daher als Konstante gepflegt (nicht aus der CSV
 * abgeleitet).
 *
 * Liegt bewusst **außerhalb von `#lib/server`**: Der Param-Matcher
 * (`src/params/bundesland.ts`) nutzt diese Helfer, und SvelteKit-Matcher laufen
 * auch im Browser – sie dürfen daher keine server-only-Module importieren.
 *
 * Die Slugs entsprechen exakt `toCitySlug(name)`; ein Test in `staedte.test.ts`
 * stellt sicher, dass diese Liste und die Bundesländer in `städte.csv` nicht
 * auseinanderlaufen.
 */
export const BUNDESLAND_BY_SLUG = {
	'baden-wuerttemberg': 'Baden-Württemberg',
	bayern: 'Bayern',
	berlin: 'Berlin',
	brandenburg: 'Brandenburg',
	bremen: 'Bremen',
	hamburg: 'Hamburg',
	hessen: 'Hessen',
	'mecklenburg-vorpommern': 'Mecklenburg-Vorpommern',
	niedersachsen: 'Niedersachsen',
	'nordrhein-westfalen': 'Nordrhein-Westfalen',
	'rheinland-pfalz': 'Rheinland-Pfalz',
	saarland: 'Saarland',
	sachsen: 'Sachsen',
	'sachsen-anhalt': 'Sachsen-Anhalt',
	'schleswig-holstein': 'Schleswig-Holstein',
	thueringen: 'Thüringen'
} as const;

/** URL-Slug eines Bundeslands, z. B. „bayern“ oder „baden-wuerttemberg“. */
export type BundeslandSlug = keyof typeof BUNDESLAND_BY_SLUG;

/** Anzeigename eines Bundeslands, z. B. „Bayern“ oder „Baden-Württemberg“. */
export type Bundesland = (typeof BUNDESLAND_BY_SLUG)[BundeslandSlug];

/** Type-Guard: Ist `slug` der Slug eines der 16 Bundesländer? */
export function isBundeslandSlug(slug: string): slug is BundeslandSlug {
	return Object.prototype.hasOwnProperty.call(BUNDESLAND_BY_SLUG, slug);
}

/**
 * Die drei Stadtstaaten (Berlin, Bremen, Hamburg). Anders als die Flächenländer
 * haben sie keine sinnvolle „Städte des Bundeslands“-Übersicht – sie *sind* im
 * Wesentlichen eine Stadt. Ihre Bundesland-URL (`/berlin`) ist daher direkt die
 * gleichnamige Stadtseite (Reroute in `src/hooks.ts`, Pfadbildung in
 * `cityPagePath`).
 *
 * Der Bundesland-Slug ist hier zugleich der Stadt-Slug
 * (`toCitySlug('Berlin') === 'berlin'` usw.), sodass die Stadt direkt aus dem
 * Slug folgt.
 */
const STADTSTAAT_SLUGS = new Set<BundeslandSlug>(['berlin', 'bremen', 'hamburg']);

/** Ist `slug` ein Stadtstaat (Berlin, Bremen, Hamburg)? */
export function isStadtstaat(slug: string): slug is BundeslandSlug {
	return STADTSTAAT_SLUGS.has(slug as BundeslandSlug);
}

/** Slug → Anzeigename, oder `null`, wenn der Slug zu keinem Bundesland gehört. */
export function bundeslandFromSlug(slug: string): Bundesland | null {
	return isBundeslandSlug(slug) ? BUNDESLAND_BY_SLUG[slug] : null;
}

/** Alle gültigen Bundesland-Slugs – Quelle der Wahrheit für Prerender/Sitemap. */
export function allBundeslandSlugs(): BundeslandSlug[] {
	return Object.keys(BUNDESLAND_BY_SLUG) as BundeslandSlug[];
}
